import React from 'react';
import { NewProjectForm } from '../components/projects';
import { useProjectStore } from '../stores/projectStore';
import { useAuth } from '../context/AuthContext';
import { useSessionActions } from '../context/SessionContext';
import { useShellUiActions } from '../context/ShellUiContext';
import { useActiveView } from '../router/useUrlNavigation';

// BAR-40 S4: /new-project route. Replaces the mainView==='new-project' ternary
// branch verbatim. The onCreated ordering (resetChatState → prefill+autoSend →
// navigate) is preserved exactly; the prefill setters come from ShellUi context
// (MainApplicationUI-owned) so they hit the SAME state GeminiChatArea reads.
export default function NewProjectRoute() {
  const { userData } = useAuth();
  const { resetChatState } = useSessionActions();
  const { setPrefillChatInput, setAutoSendPrefill, beginDraft, handleSelectProject, navigateToView } = useShellUiActions();
  const { newProjectType } = useActiveView();
  const setWorkspaceMode = useProjectStore((s) => s.setWorkspaceMode);

  return (
    <div className="h-full overflow-y-auto">
      <NewProjectForm
        userId={userData?.email || ''}
        projectType={newProjectType}
        onBoxDraft={beginDraft}
        onCreated={(projectId: string, initialMessage?: string) => {
          console.log('Project created:', projectId);
          // State-update ordering: all four setters below run inside
          // the same React event handler and rely on React 18's
          // automatic batching to land as a single render. The
          // prefill state is set BEFORE the navigation so that when
          // GeminiChatArea mounts via the view transition, its
          // auto-send effect already sees the non-null initialInput
          // and autoSendOnMount=true in the same render — no one-
          // render delay, no seed lost. resetChatState is synchronous
          // and does not touch prefillChatInput or autoSendPrefill,
          // so calling it first is safe.
          //
          // Pass the new project's ID to resetChatState so its
          // hasActiveResearch check is scoped to the right target
          // (consistent with handleSelectProject at line 157). A
          // freshly-created project can't have active research yet,
          // so the effective behaviour is unchanged — passing the
          // ID just keeps call sites uniform and prevents the
          // global isLoading reset from racing with another
          // project's in-flight research in the background.
          resetChatState(projectId);
          // Seed the workspace chat with the user's first message,
          // if any — it will auto-send on mount. Blank-project
          // creations pass no message and nothing auto-sends.
          if (initialMessage && initialMessage.trim()) {
            setPrefillChatInput(initialMessage.trim());
            setAutoSendPrefill(true);
          }
          navigateToView(newProjectType === 'build' ? 'build-artifacts' : 'workspace', { projectId });
          // Build projects start in plan mode; research-only start in research mode
          setWorkspaceMode(newProjectType === 'build' ? 'plan' : 'research');
        }}
        onProjectSelect={handleSelectProject}
      />
    </div>
  );
}
